import { Bouton } from '../ui/composants/bouton'
import { ChampRecherche } from '../ui/composants/champ-recherche'
import { Carte, EnteteSection } from '../ui/composants/entete-section'
import { CalendarClock, Coins, FileText, Hash, Percent, Plus, Ruler, Trash2, Euro } from 'lucide-react'
import { useLiveQuery } from 'dexie-react-hooks'
import { lazy, Suspense, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { LigneDevis } from '../db/modele'
import {
  donneesPdf,
  LIBELLES_STATUT,
  ligneDepuisArticle,
  copiesDuDevis,
  copierSansTva,
  nouvelleVersion,
  lignesAvecTva,
  sansTva,
  marquerEnvoye,
  marquerRefuse,
  deplacerLigne,
  modifierLignes,
  TAUX_TVA,
  totauxDevis,
  factureEnVigueur,
} from '../lib/devis'
import { situationsDuDevis } from '../lib/situations'
import { chantiersDuDevis, creerChantier } from '../lib/chantiers'
import { BlocAvenants } from '../ui/avenants'
import { lireEntreprise } from '../lib/entreprise'
import { nouvelId } from '../lib/id'
import { centimesVersEuros, eurosVersCentimes, formatEuro, lireNombre, saisieEuros, totalLigne, versSaisie } from '../lib/money'
import { noterPremierPdf } from '../lib/mesures'
import { metreCoherent } from '../lib/metre'
import { assembler } from '../lib/dictee'
import { avecRemise, estLigneRemise } from '../lib/remise'
import { FeuilleRemise } from '../ui/remise'
import { BoutonDictee } from '../ui/dictee'
import { FeuilleMetre } from '../ui/metre'
import { genererPdfDevis } from '../lib/pdf/generer'
import { versCalcul } from '../lib/pdf/devis-pdf'
import { rechercher } from '../lib/recherche'
import { getBase, type DevisLocal } from '../outbox/base'
import { creer, lire, lister, modifier, supprimer } from '../outbox/depot'
import { ApercuPdf } from '../ui/apercu-pdf'
import { useFranchiseTva } from '../ui/franchise'
import { FacturationDevis } from './FacturationDevis'
import { BarreActions, CHAMP } from '../ui/synchro'

// Pad de signature chargé à la demande (précaché pour le hors ligne)
const FeuilleSignatureEnLigne = lazy(() => import('../ui/signature-en-ligne').then((m) => ({ default: m.FeuilleSignatureEnLigne })))
const FaireSigner = lazy(() => import('../ui/signature').then((m) => ({ default: m.FaireSigner })))

const CHAMP_COMPACT = 'min-h-12 w-full rounded border-2 px-2 text-base focus:border-slate-900 focus:outline-none'

/** Champ numérique validé à la sortie du champ : la saisie n'est écrite que si elle est valide. */
function ChampNombre(props: { valeur: string; libelle: string; valider: (saisie: string) => boolean; lectureSeule: boolean }) {
  const [saisie, setSaisie] = useState(props.valeur)
  const [invalide, setInvalide] = useState(false)
  // La valeur enregistrée change (autre appareil, synchro) : la saisie la reprend. Ajusté pendant le rendu,
  // le motif que React recommande à la place d'un effet qui recopie une prop dans un état.
  const [vue, setVue] = useState(props.valeur)
  if (props.valeur !== vue) {
    setVue(props.valeur)
    setSaisie(props.valeur)
  }
  return (
    <input
      aria-label={props.libelle}
      className={`${CHAMP_COMPACT} text-right ${invalide ? 'border-red-700' : 'border-slate-400'}`}
      inputMode="decimal"
      value={saisie}
      readOnly={props.lectureSeule}
      onChange={(e) => setSaisie(e.target.value)}
      onBlur={() => setInvalide(!props.valider(saisie))}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
    />
  )
}

function Ligne(props: { ligne: LigneDevis; lectureSeule: boolean; tvaFixee: boolean; changer: (patch: Partial<LigneDevis>) => void; retirer: () => void; deplacer: (delta: -1 | 1) => void; premiere: boolean; derniere: boolean; remiser?: () => void }) {
  const { ligne, lectureSeule, changer } = props
  const [metre, setMetre] = useState(false)
  return (
    <li className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <span className="pt-3 font-semibold">{ligne.designation}</span>
        {!lectureSeule && (
          <div className="flex shrink-0 items-center">
            <button className="grid size-12 place-items-center rounded text-xl text-slate-700 disabled:opacity-30" aria-label={`Monter la ligne ${ligne.designation}`} disabled={props.premiere} onClick={() => props.deplacer(-1)}>
              ↑
            </button>
            <button className="grid size-12 place-items-center rounded text-xl text-slate-700 disabled:opacity-30" aria-label={`Descendre la ligne ${ligne.designation}`} disabled={props.derniere} onClick={() => props.deplacer(1)}>
              ↓
            </button>
            <button className="grid size-12 place-items-center rounded text-xl text-slate-700" aria-label="Retirer la ligne" onClick={props.retirer}>
              ×
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-[1fr_1.3fr_1fr] gap-2 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">Qté{ligne.unite ? ` (${ligne.unite})` : ''}</span>
          <ChampNombre
            libelle="Quantité"
            valeur={versSaisie(ligne.quantite)}
            lectureSeule={lectureSeule || estLigneRemise(ligne)}
            valider={(s) => {
              const q = lireNombre(s)
              if (!q || q.isNegative()) return false
              if (q.toNumber() !== ligne.quantite) changer({ quantite: q.toNumber() })
              return true
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">PU HT (€)</span>
          <ChampNombre
            libelle="Prix unitaire HT"
            valeur={saisieEuros(ligne.prixUnitaireHtCents)}
            lectureSeule={lectureSeule}
            valider={(s) => {
              const cents = eurosVersCentimes(s)
              if (cents === null) return false
              if (cents !== ligne.prixUnitaireHtCents) changer({ prixUnitaireHtCents: cents })
              return true
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">TVA</span>
          <SelectTva valeur={ligne.tvaPercent} desactive={lectureSeule || props.tvaFixee} changer={(t) => changer({ tvaPercent: t })} />
        </label>
      </div>
      {!lectureSeule && props.remiser && !estLigneRemise(ligne) && (
        <Bouton variante="lien" className="self-start text-sm" onClick={props.remiser} aria-label={`Faire une remise sur ${ligne.designation}`}>
          Faire une remise
        </Bouton>
      )}
      {estLigneRemise(ligne) && <p className="text-sm text-slate-700">Ligne de remise : quantité −1, seul le montant se modifie.</p>}
      {((!lectureSeule && !estLigneRemise(ligne)) || ligne.metre?.length) && (
        <div className="flex flex-wrap items-center gap-x-4">
          <Bouton variante="lien" className="self-start text-sm" onClick={() => setMetre(true)} aria-label={`${lectureSeule ? 'Voir le métré' : ligne.metre?.length ? 'Modifier le métré' : 'Calculer un métré'} de ${ligne.designation}`}>
            {lectureSeule ? 'Voir le métré' : ligne.metre?.length ? 'Modifier le métré' : 'Calculer un métré'}
          </Bouton>
          {!metreCoherent(ligne.quantite, ligne.metre) && <span className="text-sm text-slate-700">Quantité modifiée à la main depuis le métré.</span>}
        </div>
      )}
      {metre && <FeuilleMetre ligne={ligne} lectureSeule={lectureSeule} appliquer={(quantite, m) => changer({ quantite, metre: m })} fermer={() => setMetre(false)} />}
      <p className="text-right font-semibold">{formatEuro(totalLigne(versCalcul(ligne)).ht)} HT</p>
    </li>
  )
}

function SelectTva(props: { valeur: number; desactive?: boolean; changer: (t: number) => void }) {
  // Un taux hors liste (import, autre appareil) reste affiché
  const taux = TAUX_TVA.includes(props.valeur as never) ? TAUX_TVA : [...TAUX_TVA, props.valeur]
  return (
    <select
      className={`${CHAMP_COMPACT} border-slate-400 bg-white`}
      value={props.valeur}
      disabled={props.desactive}
      onChange={(e) => props.changer(Number(e.target.value))}
    >
      {taux.map((t) => (
        <option key={t} value={t}>
          {versSaisie(t)} %
        </option>
      ))}
    </select>
  )
}

/** Ligne saisie à la main, avec option d'enregistrement dans la bibliothèque. */
function FormLigneLibre(props: { ajouter: (l: LigneDevis, memoriser: boolean) => Promise<void>; fermer: () => void; franchise: boolean }) {
  const [designation, setDesignation] = useState('')
  const [quantite, setQuantite] = useState('1')
  const [unite, setUnite] = useState('u')
  const [prix, setPrix] = useState('')
  const [tva, setTva] = useState<number>(props.franchise ? 0 : 10)
  const [memoriser, setMemoriser] = useState(true)
  const q = lireNombre(quantite)
  const cents = eurosVersCentimes(prix)
  const valide = designation.trim() !== '' && q !== null && !q.isNegative() && cents !== null

  async function soumettre(e: FormEvent) {
    e.preventDefault()
    if (!valide) return
    await props.ajouter(
      { designation: designation.trim(), unite: unite.trim() || undefined, quantite: q.toNumber(), prixUnitaireHtCents: cents, tvaPercent: tva },
      memoriser,
    )
    props.fermer()
  }

  return (
    <form onSubmit={soumettre} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <label className="flex flex-col gap-1 text-sm">
        <span className="flex items-center gap-2 font-semibold"><FileText aria-hidden className="size-5" />Désignation</span>
        <input className={CHAMP} placeholder="Ex : Rénovation salle de bain, terrassement…" value={designation} onChange={(e) => setDesignation(e.target.value)} autoFocus />
      </label>
      <BoutonDictee ajouter={(t) => setDesignation((x) => assembler(x, t))} libelle="Dicter au micro" />
      <div className="grid grid-cols-2 gap-2 text-sm">
        <label className="flex flex-col gap-1">
          <span className="flex items-center gap-2 font-semibold"><Hash aria-hidden className="size-5" />Quantité</span>
          <input className={CHAMP} inputMode="decimal" value={quantite} onChange={(e) => setQuantite(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="flex items-center gap-2 font-semibold"><Ruler aria-hidden className="size-5" />Unité</span>
          <input className={CHAMP} value={unite} onChange={(e) => setUnite(e.target.value)} list="unites" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="flex items-center gap-2 font-semibold"><Euro aria-hidden className="size-5" />PU HT (€)</span>
          <input className={CHAMP} placeholder="Ex : 1250" inputMode="decimal" value={prix} onChange={(e) => setPrix(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="flex items-center gap-2 font-semibold"><Percent aria-hidden className="size-5" />TVA</span>
          <SelectTva valeur={tva} desactive={props.franchise} changer={setTva} />
        </label>
      </div>
      <label className="flex min-h-12 items-center gap-3">
        <input type="checkbox" className="size-6" checked={memoriser} onChange={(e) => setMemoriser(e.target.checked)} />
        Enregistrer aussi dans ma bibliothèque
      </label>
      <div className="flex gap-2">
        <Bouton type="button" variante="secondaire" className="flex-1" onClick={props.fermer}>
          Annuler
        </Bouton>
        <Bouton type="submit" variante="secondaire" className="flex-1" disabled={!valide}>
          <Plus aria-hidden />
          Ajouter la ligne
        </Bouton>
      </div>
    </form>
  )
}

/**
 * Mentions propres à ce devis : délai ou date de début d'exécution (imprimé sur le PDF) et, pour une vente à un
 * particulier hors établissement, le droit de rétractation et son formulaire type. Modifiables en brouillon seulement.
 */
function ConditionsDevis({ devis, lectureSeule, franchise }: { devis: DevisLocal; lectureSeule: boolean; franchise: boolean }) {
  const base = getBase()
  const [delai, setDelai] = useState(devis.delaiExecution ?? '')
  if (lectureSeule && !devis.delaiExecution && !devis.horsEtablissement && !devis.autoliquidation) return null
  if (lectureSeule) {
    return (
      <Carte className="flex flex-col gap-1 text-sm">
        {devis.delaiExecution && (
          <p>
            <span className="font-semibold">Délai d’exécution :</span> {devis.delaiExecution}
          </p>
        )}
        {devis.horsEtablissement && <p>Contrat conclu hors établissement : le droit de rétractation de 14 jours et le formulaire type figurent sur le devis.</p>}
        {devis.autoliquidation && <p>Autoliquidation de la TVA : toutes les lignes sont à 0 %, la TVA est due par le preneur.</p>}
      </Carte>
    )
  }
  return (
    <Carte className="flex flex-col gap-3">
      <label className="flex flex-col gap-1">
        <span className="flex items-center gap-2 font-semibold">
          <CalendarClock aria-hidden className="size-5" />
          Délai d’exécution
        </span>
        <input
          className={CHAMP}
          maxLength={300}
          placeholder="Ex : début sous 3 semaines après acceptation, durée 2 semaines"
          value={delai}
          onChange={(e) => setDelai(e.target.value)}
          onBlur={() => delai.trim() !== (devis.delaiExecution ?? '') && void modifier(base, 'devis', devis.id, { delaiExecution: delai.trim() || undefined })}
        />
        <span className="text-sm text-slate-700">Date ou délai de début des travaux, imprimé sur le devis.</span>
      </label>
      <label className="flex min-h-12 items-center gap-3">
        <input
          type="checkbox"
          className="size-6 shrink-0"
          checked={!!devis.horsEtablissement}
          onChange={(e) => void modifier(base, 'devis', devis.id, { horsEtablissement: e.target.checked || undefined })}
        />
        <span>
          Vente à un particulier hors établissement (chez le client, démarchage)
          <span className="block text-sm text-slate-700">Ajoute au PDF le droit de rétractation de 14 jours et le formulaire type.</span>
        </span>
      </label>
      <label className="flex min-h-12 items-center gap-3">
        <input
          type="checkbox"
          className="size-6 shrink-0"
          checked={!!devis.autoliquidation}
          disabled={franchise || !!devis.avenantDe}
          onChange={(e) => {
            const coche = e.target.checked
            void modifier(base, 'devis', devis.id, (actuel) => ({ autoliquidation: coche || undefined, ...(coche && { lignes: actuel.lignes.map(sansTva) }) }))
          }}
        />
        <span>
          Autoliquidation de la TVA (sous-traitance dans le bâtiment)
          <span className="block text-sm text-slate-700">
            {franchise
              ? 'Indisponible : votre entreprise est en franchise en base de TVA.'
              : devis.avenantDe
                ? 'Un avenant suit le régime de son marché.'
                : 'Toutes les lignes passent à 0 % et la mention « TVA due par le preneur » figure sur le devis et les factures.'}
          </span>
        </span>
      </label>
    </Carte>
  )
}

/** Signature enregistrée : image, signataire, heure de l'appareil et, après synchro, scellement serveur. */
function BlocSignature({ signature }: { signature: NonNullable<DevisLocal['signature']> }) {
  const date = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
  return (
    <section className="flex flex-col gap-1 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm text-sm">
      <p className="font-semibold">Bon pour accord</p>
      <img src={signature.image} alt={`Signature de ${signature.signataire}`} className="h-24 self-start" />
      <p>
        Signé par {signature.signataire} le {date(signature.signeLe)}
      </p>
      <p className="text-slate-700">
        {signature.scelleLe ? `Scellé par le serveur le ${date(signature.scelleLe)}` : 'Sera scellé par le serveur à la prochaine synchronisation'}
      </p>
    </section>
  )
}

export default function EditionDevis() {
  const { id = '' } = useParams()
  const base = getBase()
  const naviguer = useNavigate()
  const devis = useLiveQuery(() => lire(base, 'devis', id), [id])
  const client = useLiveQuery(async () => (devis ? lire(base, 'clients', devis.clientId) : null), [devis?.clientId])
  const articles = useLiveQuery(() => lister(base, 'articles'), [], [])
  const origine = useLiveQuery(async () => (devis?.origineId ? lire(base, 'devis', devis.origineId) : null), [devis?.origineId])
  const copies = useLiveQuery(() => copiesDuDevis(base, id), [id], [])
  const situations = useLiveQuery(() => situationsDuDevis(base, id), [id], [])
  // Un avenant dont le marché a des situations s'y règle avec elles (sauf s'il a déjà été facturé à part)
  const situationsDuMarche = useLiveQuery(async () => (devis?.avenantDe ? situationsDuDevis(base, devis.avenantDe) : []), [devis?.avenantDe], [])
  const chantiers = useLiveQuery(() => chantiersDuDevis(base, id), [id], [])
  const marche = useLiveQuery(async () => (devis?.avenantDe ? lire(base, 'devis', devis.avenantDe) : null), [devis?.avenantDe])
  const franchise = useFranchiseTva()
  const [recherche, setRecherche] = useState('')
  const [ligneLibre, setLigneLibre] = useState(false)
  const [remise, setRemise] = useState<{ cible?: number } | null>(null)
  const [pdf, setPdf] = useState<Blob | null>(null)
  const [generation, setGeneration] = useState<'attente' | 'en-cours' | 'erreur'>('attente')
  const [signature, setSignature] = useState(false)
  const [signatureEnLigne, setSignatureEnLigne] = useState(false)

  if (devis === undefined) return <main className="flex-1 p-4" />
  if (devis === null) {
    return (
      <main className="flex flex-1 flex-col gap-4 p-4">
        <p>Ce devis n'existe pas ou a été supprimé.</p>
        <Bouton asChild variante="secondaire">
          <Link to="/">
            Retour aux devis
          </Link>
        </Bouton>
      </main>
    )
  }

  const d: DevisLocal = devis
  const lectureSeule = d.statut !== 'brouillon'
  // Franchise (entreprise) ou autoliquidation (ce devis) : aucune ligne ne porte de TVA
  const tvaFixe = franchise || !!d.autoliquidation
  const signable = (d.statut === 'brouillon' || d.statut === 'envoye') && d.lignes.length > 0
  const totaux = totauxDevis(d)
  // Franchise en base : un brouillon ne doit porter aucune TVA, sinon le PDF se contredirait
  const tvaAEnlever = franchise && !lectureSeule ? lignesAvecTva(d.lignes) : 0
  // Devis déjà émis (immuable) qui porte de la TVA : on propose une copie en brouillon à 0 %, une seule
  const emisAvecTva = franchise && lectureSeule && lignesAvecTva(d.lignes) > 0 && copies.length === 0
  // Une seule version en brouillon à la fois ; la copie à 0 % (franchise) a son propre bandeau
  const versionPossible = lectureSeule && !d.avenantDe && !emisAvecTva && !copies.some((c) => c.statut === 'brouillon')
  const resultats = recherche.trim()
    ? rechercher(articles, recherche, (a) => [a.designation, a.reference]).slice(0, 6)
    : []

  const ecrireLignes = (f: (lignes: LigneDevis[]) => LigneDevis[]) => modifierLignes(base, d.id, f)

  async function ajouterLibre(l: LigneDevis, memoriser: boolean) {
    let ligne = tvaFixe ? sansTva(l) : l
    if (memoriser) {
      const article = await creer(base, 'articles', {
        id: nouvelId(),
        designation: l.designation,
        unite: l.unite ?? 'u',
        prixVenteHtCents: l.prixUnitaireHtCents,
        tvaPercent: ligne.tvaPercent,
      })
      ligne = { ...ligne, articleId: article.id, unite: article.unite }
    }
    await ecrireLignes((lignes) => [...lignes, ligne])
  }

  // Clic 2 du parcours Express : PDF créé sur l'appareil, aperçu immédiat
  async function genererPdf() {
    setGeneration('en-cours')
    try {
      setPdf(await genererPdfDevis(donneesPdf(d, client ?? null, (await lireEntreprise(base)) ?? undefined, marche)))
      void noterPremierPdf(base, d) // mesure d'usage locale (voir Compte)
      setGeneration('attente')
    } catch {
      setGeneration('erreur')
    }
  }

  async function copierSansTvaEtOuvrir() {
    const copie = await copierSansTva(base, d)
    naviguer(`/devis/${copie.id}`)
  }

  async function creerNouvelleVersion() {
    const version = await nouvelleVersion(base, d)
    naviguer(`/devis/${version.id}`)
  }

  async function supprimerBrouillon() {
    if (!confirm('Supprimer ce brouillon de devis ?')) return
    await supprimer(base, 'devis', d.id)
    naviguer('/', { replace: true })
  }

  return (
    <>
      <main className="flex flex-1 flex-col gap-4 p-4">
        <EnteteSection
          Icone={FileText}
          titre={client?.nom ?? 'Client'}
          description={
            <>
              {client && (
                <Link to={`/clients/${client.id}`} className="block underline">
                  Fiche client (adresse, SIRET)
                </Link>
              )}
              <p>
                {d.avenantDe && 'Avenant · '}
                {d.numero} · {LIBELLES_STATUT[d.statut]}
              </p>
              {d.avenantDe && (
                <p className="text-nuit">
                  Avenant au devis{' '}
                  <Link to={`/devis/${d.avenantDe}`} className="underline">
                    {marche?.numero ?? 'd’origine'}
                  </Link>
                  .
                </p>
              )}
            </>
          }
        />
        {origine && (
          <p className="text-sm">
            Copie du devis{' '}
            <Link to={`/devis/${origine.id}`} className="underline">
              {origine.numero}
            </Link>{' '}
            ({LIBELLES_STATUT[origine.statut].toLowerCase()}).
          </p>
        )}
        {copies.length > 0 && (
          <p className="text-sm">
            {copies.length > 1 ? 'Copies' : 'Copie'} :{' '}
            {copies.map((c, i) => (
              <span key={c.id}>
                {i > 0 && ', '}
                <Link to={`/devis/${c.id}`} className="underline">
                  {c.numero}
                </Link>{' '}
                ({LIBELLES_STATUT[c.statut].toLowerCase()})
              </span>
            ))}
            .
          </p>
        )}
        {lectureSeule && (
          <p className="rounded-2xl bg-slate-100 p-3 text-sm">Devis {LIBELLES_STATUT[d.statut].toLowerCase()} : il n'est plus modifiable.</p>
        )}
        {d.signature && <BlocSignature signature={d.signature} />}

        {versionPossible && (
          <Bouton variante="secondaire" onClick={creerNouvelleVersion}>
            Créer une nouvelle version
          </Bouton>
        )}
        {emisAvecTva && (
          <div className="flex flex-col gap-2 rounded-2xl border-2 border-nuit bg-slate-100 p-3 text-sm" role="alert">
            <p>
              Ce devis porte de la TVA alors que votre entreprise est en franchise en base de TVA. Il ne peut plus être modifié : créez une copie à 0 %, à renvoyer à votre client.
              Le devis actuel reste inchangé.
            </p>
            <Bouton variante="secondaire" onClick={copierSansTvaEtOuvrir}>
              Créer une copie à 0 % de TVA
            </Bouton>
          </div>
        )}
        {tvaAEnlever > 0 && (
          <div className="flex flex-col gap-2 rounded-2xl border-2 border-nuit bg-slate-100 p-3 text-sm" role="alert">
            <p>
              Votre entreprise est en franchise en base de TVA : {tvaAEnlever} ligne{tvaAEnlever > 1 ? 's portent' : ' porte'} encore de la TVA. Le PDF reste bloqué tant
              qu'elle n'est pas à 0 %.
            </p>
            <Bouton variante="secondaire" onClick={() => ecrireLignes((lignes) => lignes.map(sansTva))}>
              Passer toutes les lignes à 0 %
            </Bouton>
          </div>
        )}
        <ul className="flex flex-col gap-2">
          {d.lignes.map((l, i) => (
            <Ligne
              // Clé dépendant du contenu : la ligne se réaffiche si un autre appareil la modifie
              key={`${i}:${JSON.stringify(l)}`}
              ligne={l}
              lectureSeule={lectureSeule}
              tvaFixee={tvaFixe}
              changer={(patch) => ecrireLignes((lignes) => lignes.map((x, j) => (j === i ? { ...x, ...patch } : x)))}
              retirer={() => ecrireLignes((lignes) => lignes.filter((_, j) => j !== i))}
              deplacer={(delta) => ecrireLignes((lignes) => deplacerLigne(lignes, i, delta))}
              premiere={i === 0}
              derniere={i === d.lignes.length - 1}
              remiser={() => setRemise({ cible: i })}
            />
          ))}
        </ul>

        {remise && <FeuilleRemise lignes={d.lignes} cible={remise.cible} appliquer={async (p) => void (await ecrireLignes((lignes) => avecRemise(lignes, p.replace(',', '.'), remise.cible)))} fermer={() => setRemise(null)} />}

        {!lectureSeule &&
          (ligneLibre ? (
            <FormLigneLibre ajouter={ajouterLibre} fermer={() => setLigneLibre(false)} franchise={tvaFixe} />
          ) : (
            <section className="flex flex-col gap-2">
              <ChampRecherche libelle="Ajouter une ligne depuis ma bibliothèque" placeholder="Ma bibliothèque : ex. chauffe-eau 200" value={recherche} onChange={setRecherche} />
              <ul className="flex flex-col gap-2">
                {resultats.map((a) => (
                  <li key={a.id}>
                    <button
                      className="flex min-h-12 w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm"
                      onClick={async () => {
                        await ecrireLignes((lignes) => [...lignes, tvaFixe ? sansTva(ligneDepuisArticle(a)) : ligneDepuisArticle(a)])
                        setRecherche('')
                      }}
                    >
                      <span>{a.designation}</span>
                      <span className="shrink-0 text-sm text-slate-700">
                        {formatEuro(centimesVersEuros(a.prixVenteHtCents))} / {a.unite}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {recherche.trim() && resultats.length === 0 && (
                <p className="text-sm text-slate-700">Rien dans la bibliothèque pour « {recherche.trim()} ».</p>
              )}
              <Bouton variante="secondaire" onClick={() => setLigneLibre(true)}>
                + Ligne libre
              </Bouton>
              {d.lignes.some((l) => !estLigneRemise(l)) && (
                <Bouton variante="secondaire" onClick={() => setRemise({})}>
                  Remise sur tout le devis
                </Bouton>
              )}
            </section>
          ))}

        <ConditionsDevis key={`${d.delaiExecution ?? ''}|${!!d.horsEtablissement}|${!!d.autoliquidation}|${d.statut}`} devis={d} lectureSeule={lectureSeule} franchise={franchise} />

        <Carte className="flex items-center gap-3">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-nuit">
            <Coins aria-hidden className="size-6" />
          </span>
          <div className="flex-1">
            <p className="flex justify-between text-slate-700">
              <span>Total HT</span>
              <span>{formatEuro(totaux.ht)}</span>
            </p>
            {totaux.ventilation.map((v) => (
              <p key={v.tauxPercent.toString()} className="flex justify-between text-sm text-slate-700">
                <span>TVA {versSaisie(v.tauxPercent)} %</span>
                <span>{formatEuro(v.tva)}</span>
              </p>
            ))}
            <p className="flex justify-between text-xl font-bold">
              <span>Total TTC</span>
              <span>{formatEuro(totaux.ttc)}</span>
            </p>
          </div>
        </Carte>

        {generation === 'erreur' && (
          <p className="rounded border-2 border-red-700 bg-red-50 p-3 text-sm text-red-900">
            Le PDF n'a pas pu être généré. Réessayez ; le devis reste enregistré.
          </p>
        )}
        {d.statut === 'signe' && !d.avenantDe && !factureEnVigueur(d) && (
          <Bouton asChild variante="secondaire">
            <Link to={`/devis/${d.id}/situations`}>
              Situations de travaux
            </Link>
          </Bouton>
        )}
        {d.statut === 'signe' && !d.avenantDe && <BlocAvenants base={base} marche={d} />}
        {d.statut === 'signe' && !d.avenantDe && (
          <>
            {chantiers.map((c) => (
              <Bouton key={c.id} asChild variante="secondaire">
                <Link to={`/chantiers/${c.id}`}>Chantier : {c.nom}</Link>
              </Bouton>
            ))}
            {chantiers.length === 0 && (
              <Bouton
                variante="secondaire"
                onClick={async () => {
                  const c = await creerChantier(base, { nom: client?.nom ?? d.numero, devis: d })
                  naviguer(`/chantiers/${c.id}`)
                }}
              >
                Créer le chantier
              </Bouton>
            )}
          </>
        )}
        {d.avenantDe && d.statut === 'signe' && !d.factures?.length && situationsDuMarche.length > 0 && (
          <p className="rounded-2xl border-2 border-nuit bg-slate-100 p-3 text-sm" role="status">
            Les situations de travaux du marché ont commencé : cet avenant en fait partie et se facture avec elles.{' '}
            <Link to={`/devis/${d.avenantDe}/situations`} className="underline">
              Voir les situations
            </Link>
          </p>
        )}
        <FacturationDevis devis={d} client={client ?? null} situations={d.avenantDe && !d.factures?.length ? situationsDuMarche.length : situations.length} franchise={franchise} />
        {d.statut === 'brouillon' && signable && (
          <Bouton variante="secondaire" onClick={() => setSignature(true)}>
            Faire signer sur place
          </Bouton>
        )}
        {d.statut === 'envoye' && (
          <div className="flex flex-col gap-2">
            <Bouton variante="secondaire" onClick={() => setSignatureEnLigne(true)}>
              Envoyer le lien de signature en ligne
            </Bouton>
            <Bouton variante="secondaire" onClick={genererPdf} disabled={generation === 'en-cours'}>
              Revoir ou renvoyer le PDF
            </Bouton>
            <Bouton
              variante="lien" className="self-start text-sm"
              onClick={() => confirm('Le client a refusé ce devis ?') && marquerRefuse(base, d.id)}
            >
              Refusé par le client
            </Bouton>
          </div>
        )}
        {!lectureSeule && (
          <Bouton variante="lienDanger" className="self-start text-sm" onClick={supprimerBrouillon}>
            <Trash2 aria-hidden className="size-4" />
            Supprimer le brouillon
          </Bouton>
        )}
      </main>

      <BarreActions>
        {d.statut === 'envoye' ? (
          <Bouton className="flex-1" onClick={() => setSignature(true)} disabled={!signable}>
            Faire signer
          </Bouton>
        ) : (
          <Bouton className="flex-1" onClick={genererPdf} disabled={d.lignes.length === 0 || generation === 'en-cours' || tvaAEnlever > 0}>
            {generation === 'en-cours' ? 'Génération…' : d.statut === 'brouillon' ? 'Générer le PDF' : 'Voir le PDF'}
          </Bouton>
        )}
      </BarreActions>

      <datalist id="unites">
        {['u', 'm', 'm²', 'm³', 'ml', 'kg', 'h', 'forfait'].map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>
      {pdf && (
        <ApercuPdf
          pdf={pdf}
          nom={`Devis ${d.numero}.pdf`}
          sujet={`Devis ${d.numero}`}
          destinataire={client?.email}
          fermer={() => setPdf(null)}
          marquerEnvoye={
            d.statut === 'brouillon'
              ? async () => {
                  await marquerEnvoye(base, d.id)
                  setPdf(null)
                }
              : undefined
          }
        />
      )}
      {signatureEnLigne && (
        <Suspense fallback={null}>
          <FeuilleSignatureEnLigne devis={d} client={client ?? null} fermer={() => setSignatureEnLigne(false)} />
        </Suspense>
      )}
      {signature && (
        <Suspense fallback={null}>
          <FaireSigner devis={d} client={client ?? null} fermer={() => setSignature(false)} />
        </Suspense>
      )}
    </>
  )
}
